import { type ReactNode, useCallback, useState } from 'react';
import {
  Box,
  Button,
  ButtonIcon,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
  Flex,
  Link,
  Tag,
  TagGroup,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@backstage/ui';
import { RiFileCopyLine, RiCheckLine } from '@remixicon/react';
import styles from './infraCard.module.css';

export { styles as infraCardStyles };

export const InfraStack = ({ children }: { children: ReactNode }) => (
  <div className={styles.stack}>{children}</div>
);

export const InfraSection = ({
  title,
  children,
}: {
  title: ReactNode;
  children: ReactNode;
}) => (
  <Box className={styles.infoBox}>
    <Text variant="title-small" className={styles.sectionTitle}>
      {title}
    </Text>
    {children}
  </Box>
);

export const InfraField = ({
  label,
  value,
  mono = true,
}: {
  label: string;
  value: ReactNode;
  mono?: boolean;
}) => (
  <Text variant="body-medium" className={styles.fieldRow}>
    <span className={styles.label}>{label}</span>
    <span className={mono ? styles.value : undefined}>{value}</span>
  </Text>
);

export const InfraTagRow = ({ children }: { children: ReactNode }) => (
  <TagGroup className={styles.tagRow}>{children}</TagGroup>
);

/** Direct child of InfraTagRow — do not wrap in TagGroup (react-aria collection item). */
export const InfraTag = ({ children }: { children: ReactNode }) => (
  <Tag>{children}</Tag>
);

/** Lone tag in a field — single TagGroup wrapper (required outside InfraTagRow). */
export const InfraTagValue = ({ children }: { children: ReactNode }) => (
  <TagGroup className={styles.tagRow}>
    <Tag>{children}</Tag>
  </TagGroup>
);

export const InfraCodeBlock = ({
  children,
  variant = 'light',
}: {
  children: ReactNode;
  /** Light blocks suit info cards; dark is for terminal-style snippets only. */
  variant?: 'dark' | 'light';
}) => (
  <div
    className={
      variant === 'light'
        ? `${styles.codeBlock} ${styles.codeBlockLight}`
        : styles.codeBlock
    }
  >
    {children}
  </div>
);

export const InfraDivider = () => <hr className={styles.divider} />;

export const InfraProgress = ({
  value,
  tone = 'default',
}: {
  value: number;
  tone?: 'default' | 'warning' | 'danger';
}) => {
  const fillClass = (() => {
    if (tone === 'danger') {
      return `${styles.progressFill} ${styles.progressFillDanger}`;
    }
    if (tone === 'warning') {
      return `${styles.progressFill} ${styles.progressFillWarning}`;
    }
    return styles.progressFill;
  })();
  return (
    <div className={styles.progressTrack}>
      <div className={fillClass} style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
    </div>
  );
};

export const CopyButton = ({
  text,
  label = 'Copy',
}: {
  text: string;
  label?: string;
}) => {
  const [copied, setCopied] = useState(false);

  const onCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [text]);

  return (
    <TooltipTrigger>
      <ButtonIcon aria-label={label} onPress={onCopy}>
        {copied ? <RiCheckLine size={16} /> : <RiFileCopyLine size={16} />}
      </ButtonIcon>
      <Tooltip>{copied ? 'Copied' : label}</Tooltip>
    </TooltipTrigger>
  );
};

export const CopyableValue = ({
  label,
  value,
}: {
  label: string;
  value: string;
}) => (
  <Text variant="body-medium" className={styles.fieldRow}>
    <span className={styles.label}>{label}</span>
    <span className={styles.copyRow}>
      <span className={styles.value}>{value}</span>
      <CopyButton text={value} />
    </span>
  </Text>
);

export const ExternalLink = ({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}) => (
  <Link href={href} target="_blank" rel="noopener noreferrer">
    {children}
  </Link>
);

export {
  Box,
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
  Flex,
  Link,
  Tag,
  Text,
  Tooltip,
  TooltipTrigger,
};
